/**
 * Theme preference.
 *
 * Three states rather than two, because "follow the OS" is a real answer and not the
 * absence of one — someone whose machine switches at sunset wants the app to switch
 * with it. The stylesheet is already built for exactly this: `:root` carries the light
 * palette, a `prefers-color-scheme: dark` block applies the dark one *unless* the
 * document is explicitly light, and `:root[data-theme='dark']` applies it explicitly.
 * So "system" is the absence of the attribute, and the two explicit choices set it.
 */

export type Theme = 'system' | 'light' | 'dark'

export const THEMES: Theme[] = ['system', 'light', 'dark']

/** Shared with the pre-paint script in `index.html`; changing it here changes nothing there. */
export const THEME_STORAGE_KEY = 'ga4:theme'

function isTheme(value: unknown): value is Theme {
  return value === 'system' || value === 'light' || value === 'dark'
}

export function storedTheme(): Theme {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY)
    return isTheme(raw) ? raw : 'system'
  } catch {
    // Private browsing and blocked storage both throw here. A theme is not worth
    // failing a page load over; the OS preference is a reasonable answer anyway.
    return 'system'
  }
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement
  if (theme === 'system') root.removeAttribute('data-theme')
  else root.dataset.theme = theme

  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    /* the choice still applies to this tab */
  }
}
